/**
 * JSON Schema validator using json-schema-library.
 * Validates that a given object is a structurally valid JSON Schema document
 * by leveraging the library's built-in schema validation (schemaErrors).
 */
import { compileSchema, type JsonError } from "json-schema-library";

export interface JsonSchemaValidationResult {
    valid: boolean;
    errors: string[];
}

const ok: JsonSchemaValidationResult = Object.freeze<JsonSchemaValidationResult>({ valid: true, errors: [] });

function fail(...errors: string[]): JsonSchemaValidationResult {
    return { valid: false, errors };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

function formatSchemaErrors(errors: JsonError[]): string[] {
    return errors.map((e) => String(e.data?.message ?? e.message));
}

function parseJson(raw: string): { parsed: unknown } | JsonSchemaValidationResult {
    try {
        return { parsed: JSON.parse(raw) };
    } catch {
        return fail("Invalid JSON");
    }
}

/**
 * Validates that a parsed JSON value is a structurally valid JSON Schema.
 */
function validateJsonSchema(schema: unknown): JsonSchemaValidationResult {
    if (typeof schema === "boolean") {
        return ok;
    }

    if (!isPlainObject(schema)) {
        return fail("Schema must be an object or boolean");
    }

    const node = compileSchema(schema);
    const schemaErrors = node.schemaErrors ?? [];
    if (schemaErrors.length > 0) {
        return { valid: false, errors: formatSchemaErrors(schemaErrors) };
    }

    return ok;
}

/**
 * Validates a JSON string as a JSON Schema.
 * Returns parse errors if the string is not valid JSON.
 */
function validateJsonSchemaString(
    raw: string
): JsonSchemaValidationResult {
    const result = parseJson(raw);
    if ("valid" in result) {
        return result;
    }

    return validateJsonSchema(result.parsed);
}

/**
 * Validates an MCP Trigger input schema string.
 * Must be valid JSON, a valid JSON Schema, and have `"type": "object"` at root.
 */
export function validateMcpInputSchema(
    raw: string
): JsonSchemaValidationResult {
    const result = parseJson(raw);
    if ("valid" in result) {
        return result;
    }

    const parsed = result.parsed;
    if (!isPlainObject(parsed)) {
        return fail("Input schema must be a JSON object");
    }

    if (parsed.type !== "object") {
        return fail(
            'Input schema must have "type": "object" at root (MCP tools require object parameters)'
        );
    }

    return validateJsonSchema(parsed);
}

/**
 * Validates an MCP Trigger output schema string (optional, more permissive).
 * Must be valid JSON and a valid JSON Schema, but any root type is allowed.
 */
export function validateMcpOutputSchema(
    raw: string
): JsonSchemaValidationResult {
    return validateJsonSchemaString(raw);
}
